import SubBagInputGrid from "@/features/inventory/components/form/inspection/SubBagInputGrid";
import SubInspectionTable from "@/features/inventory/components/form/inspection/SubInspectionTable";
import { FormMain } from "@/features/standard/form/FormMain";
import { FormWrapper } from "@/features/standard/form/FormWrapper";
import { PartnerAutoComplete } from "@/features/ui/autoComplete/partner/partner-autoComplete";
import { setDateFormatString } from "@/utils/dateTimeUtil";
import { Button, Flex, NumberInput, Table, Text, TextInput, Title } from "@mantine/core";
import { useForm } from "@mantine/form";
import {
  IncomingInspectionsGet200ResponseRowsInner,
  IncomingInspectionsGetRequest,
} from "@sizlcorp/sizl-api-document/dist/models";
import { IconPrinter } from "@tabler/icons-react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { FormatterProps } from "react-data-grid";

interface IncomingInspectionUpdateFormProps {
  formatterProps: FormatterProps<IncomingInspectionsGet200ResponseRowsInner>;
}

const IncomingInspectionDetail = Object.assign({}, FormMain, {
  Wrapper: FormWrapper,
  Table: SubInspectionTable,
  Input: TextInput,
  NumberInput: NumberInput,
  Button: Button,
  SubBagInputGrid: SubBagInputGrid,
});

const BAG_NUM = 40;

export const IncomingInspectionDetailForm = ({
  formatterProps,
}: IncomingInspectionUpdateFormProps) => {
  const { row } = formatterProps;

  const form = useForm<IncomingInspectionsGetRequest>({
    initialValues: {
      ...row,
      partnerCode: row.partnerCode as string,
      itemCode: row.itemCode as string,
    },
    validate: {
      lotId: (value) => (value ? null : "로트번호를 입력해주세요."),
      partnerCode: (value) => (value ? null : "거래처를 선택해주세요."),
    },
  });

  const defectBagCount = form.values.details?.filter((bag) => bag.isReturned).length;
  const goodQuantity = form.values.details?.length
    ? form.values.details?.reduce(
        (sum, bag) => (!bag.isReturned ? Number(sum) + Number(bag.quantity ?? 0) : Number(sum)),
        0,
      )
    : 0;
  const defectQuantity = form.values.details?.reduce(
    (sum, bag) => (bag.isReturned ? Number(sum) + Number(bag.quantity ?? 0) : Number(sum)),
    0,
  );

  const goodBagCount = form.values.details?.filter((bag) => !bag.isReturned && bag.quantity).length;

  const downloadPDF = () => {
    const input = document.getElementById("pdf-content");
    if (input) {
      html2canvas(input, {
        scale: 2,
        ignoreElements: (element) => element.classList.contains("no-print"),
      }).then((canvas) => {
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF({
          orientation: "portrait",
          unit: "mm",
          format: "a4",
        });

        const imgProps = pdf.getImageProperties(imgData);
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();

        const imgWidth = pdfWidth; // 너비 꽉 채움
        const imgHeight = (imgProps.height * imgWidth) / imgProps.width;

        // 세로 가운데 정렬 (X는 0, Y만 조정)
        const x = 0;
        const y = (pdfHeight - imgHeight) / 2;

        pdf.addImage(imgData, "PNG", x, y, imgWidth, imgHeight);
        pdf.save("입고수입검사_내역리스트.pdf");
      });
    }
  };

  return (
    <div id="pdf-content">
      <IncomingInspectionDetail.Wrapper miw={1000} maw={1600}>
        <Title underline align="center">
          {"입고 / 수입 검사 내역서"}
        </Title>
        <Flex justify={"flex-end"}>
          <Button
            onClick={downloadPDF}
            leftIcon={<IconPrinter />}
            color="teal"
            className="no-print"
          >
            출력
          </Button>
        </Flex>

        <Flex fw={"bold"} mb={"xl"} align={"center"} justify={"space-between"}>
          <Flex align={"center"}>
            <Text pr={"xs"} w={"5rem"}>
              거래처 :{" "}
            </Text>
            <PartnerAutoComplete
              disabled
              placeholder="거래처 선택"
              {...form.getInputProps(`partnerCode`)}
              setName={(name: string) => form.setFieldValue("partnerName", name)}
            />
          </Flex>
          <Flex gap={"xl"}>
            <Text>입고 일자 : {setDateFormatString(row.inspectionAt)}</Text>
            <Text>로트No. : {row.lotData?.name ?? row.lotData?.expiration ?? "-"}</Text>
          </Flex>
        </Flex>
        <Table withBorder withColumnBorders highlightOnHover>
          <thead>
            <tr>
              <th style={{ textAlign: "center", fontWeight: "bold" }}>{"품명"}</th>
              <th style={{ textAlign: "center", fontWeight: "bold" }}>{"Grade"}</th>
              <th style={{ textAlign: "center", fontWeight: "bold" }}>{"Color"}</th>
              <th style={{ textAlign: "center", fontWeight: "bold" }}>{"형태"}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ textAlign: "center" }}>{row.itemWithGrade?.name ?? "-"}</td>
              <td style={{ textAlign: "center" }}>{row.itemWithGrade?.gradeName ?? "-"}</td>
              <td style={{ textAlign: "center" }}>{row.itemWithGrade?.itemCategoryName ?? "-"}</td>
              <td style={{ textAlign: "center" }}>{row.itemWithGrade?.abstractItemCode ?? "-"}</td>
            </tr>
          </tbody>
        </Table>
        <IncomingInspectionDetail.Table
          disabled
          title="수입검사"
          columns={["RoHS 확인", "재질(연소)", "포장상태", "수분상태"]}
          form={form}
        />
        <Table withBorder withColumnBorders highlightOnHover>
          <tbody>
            <tr>
              <td>
                <Text align="center" fw={"bold"}>
                  {"양품수량"}
                </Text>
              </td>
              <td>
                <Text align="center" fw={"bold"}>
                  <IncomingInspectionDetail.Input disabled value={goodQuantity ?? 0} />
                </Text>
              </td>
              <td>
                <Text align="center" fw={"bold"}>
                  {"반품수량"}
                </Text>
              </td>
              <td>
                <Text align="center" fw={"bold"}>
                  <IncomingInspectionDetail.Input disabled value={defectQuantity ?? 0} />
                </Text>
              </td>
            </tr>
            <tr>
              <td>
                <Text align="center" fw={"bold"}>
                  {"양품 Bag수"}
                </Text>
              </td>
              <td>
                <Text align="center" fw={"bold"}>
                  <IncomingInspectionDetail.Input disabled value={goodBagCount ?? 0} />
                </Text>
              </td>
              <td>
                <Text align="center" fw={"bold"}>
                  {"반품 Bag수"}
                </Text>
              </td>
              <td>
                <Text align="center" fw={"bold"}>
                  <IncomingInspectionDetail.Input disabled value={defectBagCount ?? 0} />
                </Text>
              </td>
            </tr>
            <tr>
              <td>
                <Text align="center" fw={"bold"}>
                  {"반품 사유"}
                </Text>
              </td>
              <td colSpan={3}>
                <IncomingInspectionDetail.Input
                  disabled
                  placeholder="반품 사유를 입력해주세요"
                  {...form.getInputProps("defectReason")}
                />
              </td>
            </tr>
          </tbody>
        </Table>
        <IncomingInspectionDetail.SubBagInputGrid disabled bagNum={BAG_NUM} form={form} />
      </IncomingInspectionDetail.Wrapper>
    </div>
  );
};
